import React from 'react';

// The edl® wordmark, traced from edl.dk. Always white on black in the shell
// (brand.edl.dk/logo: white on dark, never recoloured).
export function Logo({ height = 32, title = 'edl' }) {
  return (
    <svg className="edl-logo" viewBox="0 0 64 32" height={height} width={(height * 64) / 32} role="img" aria-label={title}>
      <path
        fill="currentColor"
        d="M 20.866 20.251 C 20.866 13.06 16.363 8.769 10.212 8.769 C 3.86 8.769 0 13.457 0 19.456 C 0 25.733 3.94 30.5 10.815 30.5 C 15.479 30.5 18.936 28.315 20.343 23.866 L 14.715 23.468 C 14.031 25.256 12.664 26.13 10.695 26.13 C 7.88 26.13 6.071 24.303 5.83 21.204 L 20.866 21.204 Z M 10.413 12.861 C 12.986 12.861 14.675 14.53 14.996 17.39 L 5.789 17.39 C 6.071 14.57 7.84 12.861 10.413 12.861 Z M 36.345 1.5 L 36.345 11.352 C 34.938 9.644 32.968 8.769 30.314 8.769 C 25.611 8.769 21.831 12.742 21.831 19.456 C 21.831 26.051 25.53 30.46 30.756 30.46 C 33.33 30.46 35.38 29.387 36.787 27.242 L 36.988 29.785 L 42.014 29.785 L 42.014 1.5 Z M 31.963 25.534 C 29.229 25.534 27.299 23.23 27.299 19.456 C 27.299 15.722 29.229 13.417 31.963 13.417 C 34.697 13.417 36.626 15.761 36.626 19.457 C 36.626 23.151 34.697 25.534 31.963 25.534 Z M 45.219 29.785 L 50.888 29.785 L 50.888 1.5 L 45.219 1.5 Z M 58.978 9.41 C 61.2 9.41 63 7.639 63 5.455 C 63 3.269 61.2 1.5 58.978 1.5 C 56.757 1.5 54.957 3.269 54.957 5.455 C 54.957 7.639 56.757 9.409 58.979 9.409 Z M 58.978 8.672 C 57.169 8.672 55.707 7.234 55.707 5.455 C 55.707 3.675 57.169 2.236 58.979 2.236 C 60.788 2.236 62.251 3.675 62.251 5.455 C 62.251 7.234 60.788 8.672 58.979 8.672 Z M 57.617 7.235 L 58.266 7.235 L 58.266 5.924 L 59.286 5.924 L 59.98 7.234 L 60.703 7.234 L 59.914 5.785 C 60.276 5.598 60.552 5.227 60.552 4.728 C 60.552 3.987 59.955 3.586 59.397 3.586 L 57.617 3.586 Z M 58.266 5.426 L 58.266 4.085 L 59.201 4.085 C 59.523 4.085 59.849 4.268 59.849 4.728 C 59.849 5.177 59.527 5.425 59.186 5.425 L 58.266 5.425 Z"
      />
    </svg>
  );
}

// A white-label logo from the config (`brand.logo`), cleaned to a bare <svg>
// on the server. Same rule as ours: drawn in currentColor, white on black.
export function BrandLogo({ brand, height = 32 }) {
  if (!brand?.logo) return <Logo height={height} />;
  return (
    <span
      className="shell-brand-logo"
      style={{ height: brand.logoHeight ?? compactHeight(brand.logo, height) }}
      role="img"
      aria-label={brand.name ?? ''}
      dangerouslySetInnerHTML={{ __html: brand.logo }}
    />
  );
}

// `height` suits a one-line wordmark (about 4:1). A stacked or square mark at
// that height reads tiny, so it grows toward the same area, up to 1.6×.
// brand.logoHeight, when set, wins.
function compactHeight(svg, height) {
  const box = svg.match(/viewBox="\s*[-\d.]+[\s,]+[-\d.]+[\s,]+([\d.]+)[\s,]+([\d.]+)/);
  const aspect = box ? Number(box[1]) / Number(box[2]) : 0;
  if (!(aspect > 0) || aspect >= 3.5) return height;
  return Math.round(Math.min(height * 1.6, height * Math.sqrt(3.5 / aspect)));
}

// edl.dk's arrows are Feather's arrow-up-right / arrow-right.
export function Arrow({ direction = 'right' }) {
  const d = direction === 'up-right' ? 'M7 17L17 7M7 7h10v10' : 'M5 12h14M12 5l7 7-7 7';
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

// The contents of an .edl-pill__disc: the arrow plus the twin that slides in
// on hover (see brand.css). edl.dk's large content pills use "↗" even for
// in-app links; its small nav pill ("Contact") uses "→".
export function PillArrow({ direction = 'up-right' }) {
  return (
    <>
      <Arrow direction={direction} />
      <Arrow direction={direction} />
    </>
  );
}
